import { useToastContext } from '@payaca/ambrosia';
import { useEffect, useRef, useState } from 'react';

type SubmitErrorState = {
  fieldId?: string;
  message?: string;
  isRefreshing: boolean;
};

/**
 * A save can finish after the sidebar has closed or moved to another field,
 * so a refused save belongs to the field it came from. It shows inline while
 * that field is open, and as a toast otherwise.
 */
export const useSubmitErrorForField = (shown: {
  isOpen: boolean;
  customFieldId?: string;
}) => {
  const { isOpen, customFieldId } = shown;
  const { pushError } = useToastContext();
  const [state, setState] = useState<SubmitErrorState>({
    isRefreshing: false,
  });
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    setState({ isRefreshing: false });
  }, [isOpen, customFieldId]);

  const isShownField = (fieldId?: string) =>
    shownRef.current.isOpen && shownRef.current.customFieldId === fieldId;
  const isForShownField = isOpen && state.fieldId === customFieldId;

  return {
    submitError: isForShownField ? state.message : undefined,
    isRefreshing: isForShownField && state.isRefreshing,
    isShownField,
    clear: () => setState({ isRefreshing: false }),
    report: (fieldId: string | undefined, message: string) => {
      setState({ fieldId, message, isRefreshing: false });
      if (!isShownField(fieldId)) pushError(message);
    },
    whileRefreshing: async (
      fieldId: string | undefined,
      refresh: () => Promise<unknown>
    ) => {
      const setRefreshing = (isRefreshing: boolean) =>
        setState((current) =>
          current.fieldId === fieldId ? { ...current, isRefreshing } : current
        );
      setRefreshing(true);
      try {
        await refresh();
      } finally {
        setRefreshing(false);
      }
    },
  };
};
